import { ref } from 'vue'
import { apiPost } from '@/composables/useApi'
import { auth } from '@/firebase'
import { computeReferenceProgress, tokenize } from '@/utils/speech/readAlongEndpoint'

const FINISH_WAIT_MS = 900
// The scores come with the final result, which can arrive well after the last words.
const ASSESSMENT_WAIT_MS = 8000
// Azure's longest allowed pause inside one result, so a sentence is rarely split in two.
const SEGMENT_SILENCE_MS = '5000'
// Azure tokens live 10 minutes; stop reusing one a little before that.
const TOKEN_USABLE_MS = 8 * 60 * 1000

// Live Azure pronunciation assessment: the partial text tells how far the learner has
// read, and the final result carries the scores, so one recognition serves both.
//
// The backend rate-limits tokens and each one covers one spoken attempt. Issuing one
// takes a few database round trips, so it must never sit between the learner starting
// to speak and the first words: the next token is fetched as soon as the current one
// is spoken into, and the connection is opened when the learner is asked to speak.
// A token that was never spoken into is kept for the next sentence.
export function useLiveSpeechProgress() {
  let sdkPromise = null
  let heldToken = null
  let tokenRequest = null
  // Once the quota runs out, or the service keeps failing, stop
  // trying for this page and rely on the duration-based rules.
  let disabled = false
  let consecutiveFailures = 0
  // The main Azure resource still issues tokens after its quota runs out and only
  // recognition fails; from then on tokens come from the backup resource.
  let useFallback = false
  let tokenRequestFallback = false
  // 'pending' until recognition first returns text, then 'live'; 'unavailable' after disabling.
  const status = ref('pending')
  // 'limit' (quota used up) or 'error' (connection or loading failure).
  const unavailableReason = ref('')

  function disable(error) {
    disabled = true
    status.value = 'unavailable'
    unavailableReason.value = error?.status === 429 ? 'limit' : 'error'
  }

  function loadSdk() {
    if (!sdkPromise) sdkPromise = import('microsoft-cognitiveservices-speech-sdk')
    return sdkPromise
  }

  function isUsable(token) {
    return Boolean(token) && token.fallback === useFallback && Date.now() - token.fetchedAt < TOKEN_USABLE_MS
  }

  function ensureToken() {
    if (isUsable(heldToken)) return Promise.resolve(heldToken)
    if (!tokenRequest || tokenRequestFallback !== useFallback) {
      const fallback = useFallback
      const request = apiPost('/api/speech-token', fallback ? { fallback: true } : {}, { auth: Boolean(auth.currentUser) })
        .then((result) => {
          const token = { token: result.token, region: result.region, fallback, fetchedAt: Date.now() }
          if (fallback === useFallback) heldToken = token
          return token
        })
        .finally(() => {
          if (tokenRequest === request) tokenRequest = null
        })
      tokenRequest = request
      tokenRequestFallback = fallback
    }
    return tokenRequest
  }

  // Returns whether the next attempts switch to the backup resource.
  function switchToFallback(errorDetails) {
    if (useFallback || !/quota/i.test(errorDetails || '')) return false
    useFallback = true
    heldToken = null
    return true
  }

  async function takeToken() {
    const token = await ensureToken()
    if (heldToken === token) heldToken = null
    return token
  }

  function returnToken(token) {
    if (!heldToken && isUsable(token)) heldToken = token
  }

  // While the sentence plays: load the SDK and have a token ready.
  function prepare() {
    if (disabled) return
    void Promise.all([loadSdk(), ensureToken()]).catch(disable)
  }

  // When the learner is asked to speak: connect now, recognise once begin() is called.
  function openSession({ referenceText, language, onHeard }) {
    if (disabled) return null

    const referenceTokens = tokenize(referenceText)
    const pendingFrames = []
    const finalizedTexts = []
    let partialText = ''
    let token = null
    let began = false
    let recognizing = false
    let failed = false
    let closed = false
    let pushStream = null
    let recognizer = null
    const segments = []
    let resolveStopped = null
    const stopped = new Promise((resolve) => {
      resolveStopped = resolve
    })
    let resolveAssessment = null
    const assessment = new Promise((resolve) => {
      resolveAssessment = resolve
    })

    function markFailed({ countFailure = true } = {}) {
      failed = true
      session.progress = null
      if (countFailure) consecutiveFailures += 1
      if (consecutiveFailures >= 2 && !disabled) disable()
      resolveStopped()
    }

    function startRecognition() {
      if (recognizing || !recognizer) return
      recognizing = true
      pendingFrames.splice(0).forEach((pcm) => pushStream.write(pcm))
      recognizer.startContinuousRecognitionAsync(() => {}, markFailed)
    }

    const session = {
      progress: null,
      // Scores of the finished attempt, or null when there are none to report.
      assessment,
      get failed() {
        return failed
      },
      // The learner started speaking: this is the attempt the token covers.
      begin() {
        if (began) return
        began = true
        startRecognition()
        // Fetch the next sentence's token now, long before it is needed.
        void ensureToken().catch(disable)
      },
      pushFrame(frame) {
        if (!began || failed || closed) return
        const pcm = floatToPcm16(frame)
        if (recognizing) pushStream.write(pcm)
        else pendingFrames.push(pcm)
      },
      // Flush the audio and wait briefly for the final recognition result. The scores
      // may take longer; they resolve `assessment` without holding up the caller.
      async finish() {
        if (closed) return session.progress
        closed = true
        if (!recognizing) {
          dispose()
          resolveAssessment(null)
          return session.progress
        }
        pushStream.close()
        void Promise.race([stopped, wait(ASSESSMENT_WAIT_MS)]).then(() => {
          resolveAssessment(failed ? null : mergeSegments(segments, referenceTokens.length))
          dispose()
        })
        await Promise.race([stopped, wait(FINISH_WAIT_MS)])
        return session.progress
      },
      cancel() {
        closed = true
        resolveAssessment(null)
        // Nobody spoke into it, so the token still covers the next sentence.
        if (!began && token) returnToken(token)
        pushStream?.close()
        dispose()
      },
    }

    function updateProgress() {
      const heard = [...finalizedTexts, partialText].join(' ').trim()
      if (!heard) return
      session.progress = computeReferenceProgress(referenceTokens, heard)
      onHeard?.(heard)
    }

    function dispose() {
      if (!recognizer) return
      const current = recognizer
      recognizer = null
      if (recognizing) current.stopContinuousRecognitionAsync(() => current.close(), () => current.close())
      else current.close()
    }

    Promise.all([loadSdk(), takeToken()])
      .then(([sdk, takenToken]) => {
        token = takenToken
        if (closed) {
          if (!began) returnToken(token)
          return
        }
        const format = sdk.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1)
        pushStream = sdk.AudioInputStream.createPushStream(format)
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token.token, token.region)
        speechConfig.speechRecognitionLanguage = language
        speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, SEGMENT_SILENCE_MS)
        recognizer = new sdk.SpeechRecognizer(speechConfig, sdk.AudioConfig.fromStreamInput(pushStream))
        new sdk.PronunciationAssessmentConfig(
          referenceText,
          sdk.PronunciationAssessmentGradingSystem.HundredMark,
          sdk.PronunciationAssessmentGranularity.Word,
          true,
        ).applyTo(recognizer)
        sdk.PhraseListGrammar.fromRecognizer(recognizer).addPhrase(referenceText)

        recognizer.recognizing = (_sender, event) => {
          consecutiveFailures = 0
          status.value = 'live'
          partialText = event.result.text || ''
          updateProgress()
        }
        recognizer.recognized = (_sender, event) => {
          if (event.result.reason === sdk.ResultReason.RecognizedSpeech && event.result.text) {
            finalizedTexts.push(event.result.text)
            const segment = readSegment(
              event.result.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult),
            )
            if (segment) segments.push(segment)
          }
          partialText = ''
          updateProgress()
        }
        recognizer.canceled = (_sender, event) => {
          // A used-up main quota loses only this attempt; the next sentence uses the backup.
          if (event.reason === sdk.CancellationReason.Error) {
            markFailed({ countFailure: !switchToFallback(event.errorDetails) })
          }
          else resolveStopped()
        }
        recognizer.sessionStopped = () => resolveStopped()

        if (began) {
          startRecognition()
        } else {
          // Warm-up only: if it fails, starting recognition connects again and reports errors.
          sdk.Connection.fromRecognizer(recognizer).openConnection(() => {}, () => {})
        }
      })
      .catch((error) => {
        // No token (quota used up, backend unreachable) or the SDK failed to load.
        disable(error)
        markFailed()
      })

    return session
  }

  return { status, unavailableReason, prepare, openSession }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function roundScore(value) {
  return typeof value === 'number' ? Math.round(value) : null
}

function readSegment(rawJson) {
  const detail = JSON.parse(rawJson)
  const best = detail.NBest?.[0]
  if (!best?.PronunciationAssessment) return null
  return {
    text: detail.DisplayText || '',
    scores: best.PronunciationAssessment,
    words: (best.Words || []).map((word) => ({
      word: word.Word || '',
      accuracyScore: roundScore(word.PronunciationAssessment?.AccuracyScore),
      errorType: word.PronunciationAssessment?.ErrorType,
    })),
  }
}

// Same shape the backend returns for an uploaded recording.
function mergeSegments(segments, referenceLength) {
  if (!segments.length) return null
  if (segments.length === 1) {
    const [{ text, scores, words }] = segments
    return {
      pronunciationScore: roundScore(scores.PronScore),
      accuracyScore: roundScore(scores.AccuracyScore),
      fluencyScore: roundScore(scores.FluencyScore),
      completenessScore: roundScore(scores.CompletenessScore),
      words,
      recognizedText: text,
    }
  }

  // A long pause split the sentence. Each part was compared with the whole sentence,
  // so its own completeness and omissions are meaningless: keep the spoken words and
  // work the totals out again.
  const spoken = segments.map((segment) => segment.words.filter((word) => word.errorType !== 'Omission'))
  const spokenCount = spoken.reduce((total, words) => total + words.length, 0)
  if (!spokenCount) return null
  const weighted = (name) => segments.reduce(
    (total, segment, index) => total + (segment.scores[name] || 0) * spoken[index].length,
    0,
  ) / spokenCount
  const matchedCount = spoken.flat().filter((word) => word.errorType !== 'Insertion').length
  const accuracy = weighted('AccuracyScore')
  const fluency = weighted('FluencyScore')
  const completeness = Math.min(100, (matchedCount / Math.max(1, referenceLength)) * 100)
  const [lowest, middle, highest] = [accuracy, fluency, completeness].sort((a, b) => a - b)
  return {
    pronunciationScore: Math.round(lowest * 0.6 + middle * 0.2 + highest * 0.2),
    accuracyScore: Math.round(accuracy),
    fluencyScore: Math.round(fluency),
    completenessScore: Math.round(completeness),
    words: spoken.flat(),
    recognizedText: segments.map((segment) => segment.text).join(' '),
  }
}

function floatToPcm16(frame) {
  const pcm = new Int16Array(frame.length)
  for (let index = 0; index < frame.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, frame[index]))
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return pcm.buffer
}
